import type {
  DSLContext,
  DSLReadRequest,
  DSLReadResponse,
} from '@commenda/types/src/commenda-os-dsl';
import { fetcher } from '@commenda/utils';
import useSWR from 'swr';
import { useApiScope } from '../scope';
import { sanitizeDSLContext } from './context';

const BASE_URL = 'dsl';

/**
 * Hook to read data from Commenda OS via DSL
 *
 * @param path - The DSL path to read (e.g., 'corporation.generalCorporationInfo.legalName')
 * @param context - The DSL context with entity IDs
 * @param options - Additional options
 * @returns The data at the specified path
 */
export const useCommendaOSRead = <T = unknown>(
  path: string | null | undefined,
  context: DSLContext | null | undefined,
  options?: {
    /** Skip the request if true */
    skip?: boolean;
    /** Revalidate on focus */
    revalidateOnFocus?: boolean;
  }
) => {
  const { shareToken } = useApiScope();

  // Only fetch if we have both path and context with companyId
  const shouldFetch = path && context?.companyId && !options?.skip;

  const cleanContext = context ? sanitizeDSLContext(context) : null;

  // Create a stable key for SWR based on path and context
  const swrKey = shouldFetch
    ? JSON.stringify({
        url: `${BASE_URL}/read`,
        path,
        context: cleanContext,
        shareToken,
      })
    : null;

  const { data, error, isLoading, mutate } = useSWR<DSLReadResponse<T>>(
    swrKey,
    async () => {
      try {
        const response = await fetcher(`${BASE_URL}/read`, {
          method: 'POST',
          body: {
            path,
            context: cleanContext,
          } as DSLReadRequest,
          shareToken,
        });

        // Parse the response JSON
        const jsonData = (await response.json()) as DSLReadResponse<T>;

        if (!response.ok) {
          throw new Error(jsonData?.message || 'Failed to read from DSL');
        }

        return jsonData;
      } catch (err) {
        console.error('[useCommendaOSRead] Error:', err);
        throw err;
      }
    },
    {
      revalidateOnFocus: options?.revalidateOnFocus ?? false,
    }
  );

  return {
    data: data?.data ?? null,
    error,
    isLoading,
    mutate,
  };
};

/**
 * Imperative function to read data from Commenda OS via DSL
 * Use this for one-off reads or when you need to read data outside of a React component
 *
 * @param path - The DSL path to read
 * @param context - The DSL context with entity IDs
 * @returns The data at the specified path
 */
export async function readCommendaOS<T = unknown>(
  path: string,
  context: DSLContext,
  shareToken: string | undefined
): Promise<T | null> {
  try {
    const cleanContext = sanitizeDSLContext(context);
    const response = await fetcher(`${BASE_URL}/read`, {
      method: 'POST',
      body: {
        path,
        context: cleanContext,
      } as DSLReadRequest,
      shareToken,
    });

    const jsonData = (await response.json()) as DSLReadResponse<T>;

    if (!response.ok) {
      throw new Error(jsonData?.message || 'Failed to read from DSL');
    }

    return jsonData.data;
  } catch (error) {
    console.error('Failed to read from Commenda OS DSL:', error);
    return null;
  }
}

/**
 * Hook to read a list of entities from Commenda OS
 * Convenience wrapper for common list operations
 *
 * @param entity - The entity type (e.g., 'corporation')
 * @param context - The DSL context with companyId
 * @param listPath - Optional DSL path to list entities (from schema listConfig.path)
 * @returns Array of entities
 */
export const useCommendaOSList = <T = unknown>(
  entity: string | null | undefined,
  context: DSLContext | null | undefined,
  listPath?: string
) => {
  // Use provided listPath or fall back to convention: company.<entity>s
  const path = listPath ?? (entity ? `company.${entity}s` : null);

  return useCommendaOSRead<T[]>(path, context);
};
